import React, {useContext, useEffect, useState} from "react";

// Services
import {NotificationsContext} from "../../../ui-components/Notifications";
import apiRequest from "../../../services/apiRequest";
import CountryContext from "../../../CountryContext";
import {useIntl} from "react-intl";

// Components
import CheckboxGroup from "../../../ui-components/CheckboxGroup";
import Placeholder from "../../../ui-components/Placeholder";
import {PageSection} from "../../../ui-components/Container";
import Select from "../../../ui-components/Select";
import Toggle from "../../../ui-components/Toggle";
import GameBarChart from "./GameBarChart";
import {WorksitesTable} from "./WorksitesTable";


// Types
import {AnalyticsGameProps, Worksite, FilterWorksites, User, GeoWorksitesData, GeoUsersData} from "./Game.type";
import {getRolesOptions, allUsers, initUsers} from "./Game.lib";
import UsersHeatMap from "../Users/UsersHeatMap";
import SearchSelect from "../../../ui-components/SearchSelect";
import CountryConfigContext from "../../../CountryConfigContext";
import Checkbox from "../../../ui-components/Checkbox";
import AuthenticationService from "../../../services/AuthenticationService";


export function AnalyticsGame(
  {
    visibilityLevel,
    visibilityUserId,
    analysisPeriod,
    worksiteTypes
  }: AnalyticsGameProps
) {

  const intl = useIntl();
  const {country} = useContext(CountryContext);
  const { config } = useContext(CountryConfigContext);
  const {push} = useContext(NotificationsContext);

  const rolesOptions = getRolesOptions(visibilityLevel)
  const userData = AuthenticationService.getUserData();
  const userScopes = AuthenticationService.getUserScopes();

  const [selectedRole, setSelectedRole] = useState(rolesOptions[0]);
  const [worksiteMeasure, setWorksiteMeasure] = useState<'val' | 'amt'>('val');
  const [worksites, setWorksites] = useState<Worksite[] | undefined>(undefined);
  const [chartWorksites, setChartWorksites] = useState<any | undefined>(undefined);

  const [filterWorksites, setFilterWorksites] = useState<FilterWorksites>({
    ownership: ['created', 'shared', 'visible'], status: ['planning', 'design', 'open', 'closed']
  });

  const [users, setUsers] = useState<User[] | undefined>(undefined);
  const [filterUsers, setFilterUsers] = useState<string[]>(initUsers);
  const [chartUsers, setChartUsers] = useState<any | undefined>(undefined);

  const [geoWorksites, setGeoWorksites] = useState<GeoWorksitesData[] | undefined>(undefined)
  const [geoUsers, setGeoUsers] = useState<GeoUsersData[] | undefined>(undefined)
  const [loadingMapUsers, setLoadingMapUsers] = useState(false);
  const [loadingWorksites, setLoadingWorksites] = useState(false);
  const [loadingMapWorksites, setLoadingMapWorksites] = useState(false);
  const [filterServices, setFilterServices] = useState<any[]>([])
  const [filterWorksiteType, setFilterWorksiteType] = useState([]);
  const [specified, setSpecified] = useState(false);
  const services = (config.services as { active: boolean; name: string }[])
    .filter((i) => i.active)
    .map((i) => i.name)
    .sort();


  useEffect(() => {
    const payload = {
      country: country.code,
      start_date: analysisPeriod.start,
      end_date: analysisPeriod.end,
      visibility_user_id: visibilityUserId,
      role: selectedRole,
    }

    if (filterServices.length) {
      payload['services'] = filterServices.map(i => i.value)
    }
    if (filterWorksiteType.length) {
      payload['worksite_types'] = filterWorksiteType.map(i => i.value)
    }
    if (specified) {
      payload['specified'] = true
    }
    const fetchData = async () => {
      setLoadingWorksites(true)
      setGeoWorksites(undefined)
      setWorksites(undefined)
      setUsers(undefined)
      try {
        const [game] = await Promise.all([
          // @ts-ignore
          apiRequest.post("/analytics/game", payload)
        ]);
        // @ts-ignore
        setWorksites(game.worksites)
        // @ts-ignore
        setUsers(game.users)
        setLoadingWorksites(false)
      } catch (error) {
        console.log(error)
        setWorksites([])
        setUsers([])
        push({title: intl.formatMessage({id: "server_error"}), type: "error"});
        setLoadingWorksites(false)
      }
    };

    fetchData()

  }, [
    visibilityUserId,
    analysisPeriod,
    selectedRole,
    specified,
    filterWorksiteType,
    filterServices,
  ]) // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    setGeoWorksites(undefined)
    setLoadingWorksites(true)
    apiRequest.post("/analytics/game/geo/worksites", {
      country: country.code,
      start_date: analysisPeriod.start,
      end_date: analysisPeriod.end,
      visibility_user_id: visibilityUserId,
      role: selectedRole,
      ownership: filterWorksites.ownership,
      status: filterWorksites.status,
      ...(filterWorksiteType.length ? {worksite_types: filterWorksiteType.map(i => i.value)} : {}),
      ...(filterServices.length ? {services: filterServices.map(i => i.value)} : {}),
      ...(specified ? {specified: true} : {})
    })
      .then((res) => {
        //@ts-ignore
        setGeoWorksites(res)
        setLoadingWorksites(false)
      })
      .catch(() => {
        push({title: intl.formatMessage({id: "server_error"}), type: "error"})
        setLoadingWorksites(false)
        setGeoWorksites([])
      })

  }, [visibilityUserId, analysisPeriod, selectedRole, filterWorksites, filterWorksiteType, filterServices, specified]);

  useEffect(() => {
    setGeoUsers(undefined)
    setLoadingMapUsers(true)
    apiRequest.post("/analytics/game/geo/users", {
      country: country.code,
      start_date: analysisPeriod.start,
      end_date: analysisPeriod.end,
      visibility_user_id: visibilityUserId,
      role: selectedRole,
      status: filterUsers,
    })
      .then((res) => {
        //@ts-ignore
        setGeoUsers(res)
      })
      .catch(() => {
        push({title: intl.formatMessage({id: "server_error"}), type: "error"})
        setGeoUsers([])
      })
      .finally(() => setLoadingMapUsers(false))

  }, [visibilityUserId, analysisPeriod, selectedRole, filterUsers]);


  useEffect(() => {
    if (worksites) {
      // const key = filterWorksites.ownership.length > 2 ? 'all' : filterWorksites.ownership[0]
      const keys = filterWorksites.ownership

      const res = worksites.map(i => {
        let row: { [k: string]: any } = {fullname: i.name, total: 0}
        filterWorksites.status.forEach(s => {
          // @ts-ignore
          // const val = i[key]?.[s][worksiteMeasure] || 0
          const val = keys.reduce((acc, key) => acc + i[key]?.[s][worksiteMeasure], 0)
          row[s] = val
          row.total += val
        })
        return row
      })

      const final = res.filter(i => i.total > 0)
        .sort((a, b) => b.total - a.total)
        .slice(0, 25)
        .map((row, index) => ({...row, fullname: `${index + 1}° ${row.fullname}`}))
      setChartWorksites(final)
    }
  }, [worksites, filterWorksites, worksiteMeasure])


  useEffect(() => {
    if (users) {
      const res = users.map(i => {
        let row: { [k: string]: any } = {fullname: i.name, total: 0}
        filterUsers.forEach(userType => {
          // @ts-ignore
          const val = i[userType]
          row[userType] = val
          row.total += val
        })
        return row
      })
      const final = res.filter(i => i.total > 0)
        .sort((a, b) => b.total - a.total)
        .slice(0, 25)
        .map((row, index) => ({...row, fullname: `${index + 1}° ${row.fullname}`}))
      setChartUsers(final)
    }
  }, [users, filterUsers])

  return <div className="w-full">
    {rolesOptions.length > 1 ?
      <div className="w-full flex flex-col items-center">
        <h1 className="text-gray-600">Players</h1>
        <Select
          label={undefined}
          value={selectedRole}
          options={rolesOptions.map(i => ({value: i, name: intl.formatMessage({id: i})}))}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setSelectedRole(e.target.value)
          }}
          className={undefined}
        />
      </div> : null
    }
    <PageSection
      title={<span className="font-bold">{intl.formatMessage({id: "worksites"})}</span>}
      description={undefined}
    >
      {worksites && chartWorksites ?
        <div className="w-full flex flex-col items-center">
          <div className="w-full flex flex-col grid grid-cols-3 sm:flex-row justify-around items-center gap-x-5">
            {/*@ts-ignore*/}
            <CheckboxGroup
              title="Ownership"
              horizontal
              options={['created', 'shared', 'visible'].map(i => ({value: i, name: intl.formatMessage({id: i})}))}
              values={filterWorksites.ownership}
              onChange={(value: string) => {
                if (filterWorksites.ownership.indexOf(value) >= 0) {
                  setFilterWorksites({
                    ...filterWorksites,
                    ownership: [...filterWorksites.ownership.filter((v) => v !== value)]
                  });
                } else {
                  setFilterWorksites({...filterWorksites, ownership: [...filterWorksites.ownership, value]});
                }
              }}
            />
              <SearchSelect
                label={intl.formatMessage({
                  id: "worksite_type",
                  defaultMessage: "Tipo cantiere",
                })}
                value={filterWorksiteType}
                onChange={(e: any) => setFilterWorksiteType(e)}
                options={worksiteTypes.map((i) => ({
                  value: i.cod_worksite_type,
                  label: i.des_worksite_type,
                }))}
                isMulti
              />
              <SearchSelect
                label={intl.formatMessage({
                  id: "configurations",
                  defaultMessage: "Configurazioni",
                })}
                value={filterServices}
                options={config.services.map((el) => ({
                  value: el.id,
                  label: el.name,
                }))}
                onChange={(e: any) => setFilterServices(e)}
                isMulti
              />
            {/*
            @TODO remove hardcoded email, used for testing
            */}
            {userScopes.includes("service_survey:read_specified") && <div className={"flex flex-col gap-y-1 pb-2 items-center justify-start"}>
              <span>{intl.formatMessage({
                id: "specified",
                defaultMessage: "specificate"
              })}</span>
              <Checkbox
                name={intl.formatMessage({id: "specified", defaultMessage: "Specificato"})}
                checked={specified}
                onChange={() => {
                  setSpecified(!specified)
                }}
              />
            </div>}
            <div className="flex flex-row gap-x-2 font-bolder text-lg justify-center">
              <span>#</span>
              <div className="-mt-5">
                {/*@ts-ignore*/}
                <Toggle
                  checked={worksiteMeasure === 'amt'}
                  onChange={() => {
                    setWorksiteMeasure(worksiteMeasure === 'val' ? 'amt' : 'val')
                  }}
                />
              </div>
              <span>€</span>
            </div>
            {/*@ts-ignore*/}
            <CheckboxGroup
              title={intl.formatMessage({id: "status", defaultMessage: "Stato"})}
              horizontal
              options={['planning', 'design', 'open', 'closed'].map(i => ({
                value: i,
                name: intl.formatMessage({id: i})
              }))}
              values={filterWorksites.status}
              onChange={(value: string) => {
                if (filterWorksites.status.indexOf(value) >= 0) {
                  setFilterWorksites({
                    ...filterWorksites,
                    status: [...filterWorksites.status.filter((v) => v !== value)]
                  });
                } else {
                  setFilterWorksites({...filterWorksites, status: [...filterWorksites.status, value]});
                }
              }}
            />
          </div>
          {!(loadingWorksites || loadingMapWorksites) ? <div className="w-full flex flex-col lg:flex-row my-4 items-center mt-5">
            <div className={`w-full ${country.code === "it" ? "lg:w-2/3" : ""}`}>
              <GameBarChart
                id="GamesWorksitesBarChart"
                data={chartWorksites}
                statusList={filterWorksites.status}
                showPrice={worksiteMeasure === 'amt'}
                intl={intl}
              />
            </div>
            {
              country.code === "it"
                ? (
                  <div className="w-full lg:w-1/3">
                    {geoWorksites ?
                      <UsersHeatMap
                        id="GeoWorksitesHeatMap"
                        data={geoWorksites.map(i => ({
                          value: worksiteMeasure === 'amt' ? i.amt_total : i.num_worksites,
                          id: 'IT-' + i.cod_province
                        }))}
                      /> :
                      null
                    }
                  </div>
                )
                : null
            }
          </div>: <Placeholder height="h-80"/>}
        </div>
        : <div>
          <Placeholder height="h-80"/>
        </div>
      }
      {userScopes.includes("game_analytics_table:read") && <div>
        <WorksitesTable
          visibility_user_id={visibilityUserId}
          country={country.code}
          start_date={analysisPeriod.start}
          end_date={analysisPeriod.end}
          role={selectedRole}
          ownership={filterWorksites.ownership}
          statuses={filterWorksites.status}
          {...(filterWorksiteType.length ? {worksite_types: filterWorksiteType.map(i => i.value)} : {})}
          {...(filterServices.length ? {services: filterServices.map(i => i.value)} : {})}
          {...(specified ? {specified: true} : {})}
        />
      </div>}
    </PageSection>
    <PageSection
      title={<span className="font-bold">{intl.formatMessage({id: "users"})}</span>}
      description={undefined}
    >
      {users && chartUsers ?
        <div className="w-full flex flex-col items-center">
          <div className="w-full flex flex-col sm:flex-row justify-around items-center">
            {/*@ts-ignore*/}
            <CheckboxGroup
              title={intl.formatMessage({id: "status", defaultMessage: "Stato"})}
              horizontal
              options={allUsers.map(i => ({value: i, name: intl.formatMessage({id: i})}))}
              values={filterUsers}
              onChange={(value: string) => {
                if (filterUsers.indexOf(value) >= 0) {
                  setFilterUsers([...filterUsers.filter((v) => v !== value)]);
                } else {
                  setFilterUsers([...filterUsers, value]);
                }
              }}
            />
          </div>
          <div className="w-full flex flex-col lg:flex-row my-4 items-center mt-5">
            <div className={`w-full ${country.code === "it" ? "lg:w-2/3" : ""}`}>
              <GameBarChart
                id="GamesUsersBarChart"
                data={chartUsers}
                statusList={filterUsers}
                showPrice={false}
                intl={intl}
              />
            </div>
            {
              country.code === "it"
                ? (
                  <div className="w-full lg:w-1/3">
                    {loadingMapUsers ? <div className="p-4">
                      <Placeholder height="h-80"/>
                    </div> : geoUsers ?
                      <UsersHeatMap
                        id="GeoUsersHeatMap"
                        data={geoUsers.map(i => ({value: i.num_users, id: 'IT-' + i.cod_province}))}
                      /> :
                      null
                    }
                  </div>
                )
                : null
            }
          </div>
        </div>
        : <div>
          <Placeholder height="h-80"/>
        </div>
      }
    </PageSection>
  </div>
}